import { useIsFocused } from '@react-navigation/native';
import { theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import { OngoingChargingSheet } from 'components/ongoingParking/OngoingChargingSheet';
import OngoingMultipleSessionsHeader from 'components/ongoingParking/OngoingMultipleSessionsHeader';
import OngoingParkingSheet from 'components/ongoingParking/OngoingParkingSheet';
import Sheet from 'components/sheets/Sheet';
import NetworkConnectionContext from 'context/NetworkConnectionContext';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useOngoingChargingSession from 'hooks/useOngoingChargingSession';
import useOngoingMultipleSession from 'hooks/useOngoingMultipleSession';
import useOngoingParkingSession from 'hooks/useOngoingParkingSession';
import useOngoingSheetState from 'hooks/useOngoingSheetState';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { StatusBar } from 'react-native';
import { parkingVehicleLicensePlate } from 'states/common';
import { useParkingStore } from 'stores/parkingStore';
import {
  checkNotificationsPermissions,
  isAndroid,
  isIos,
  isIosMax,
  isIosMini,
  isWeb,
} from 'utils/deviceUtils';

const COLLAPSED_SNAP_POINT = 100;

const OngoingSessionsSheet: React.FC<{
  showSheet?: boolean;
}> = ({ showSheet = true }) => {
  const { sessionToShow } = useOngoingMultipleSession();
  const tabBarHeight = useBottomMenuHeight();
  const selectedOngoingSessionType = useParkingStore(
    (state) => state.selectedOngoingSession
  );

  const { setParkingVehiclePlate } = useParkingStore((state) => state);
  const {
    ongoingParkingSession,
    ongoingParkingSessionError,
    ongoingParkingSessionStopPolling,
  } = useOngoingParkingSession();
  const {
    ongoingChargingSession,
    ongoingChargingSessionError,
    ongoingChargingSessionStartPolling,
    ongoingChargingSessionStopPolling,
  } = useOngoingChargingSession();
  const { isLoggedIn } = useIsLoggedIn();
  const { hasConnection } = useContext(NetworkConnectionContext);
  const isFocused = useIsFocused();
  const [ongoingParkingErrorShown, setOngoingParkingErrorShown] =
    useState(false);
  const [ongoingChargingErrorShown, setOngoingChargingErrorShown] =
    useState(false);

  const { status } = ongoingChargingSession || {};

  const isOngoingMultipleSessions = sessionToShow === 'MULTIPLE';

  const snappingPoints = useMemo(() => {
    const minSnapPoint =
      tabBarHeight > 0
        ? COLLAPSED_SNAP_POINT + tabBarHeight
        : COLLAPSED_SNAP_POINT;
    if (isWeb) {
      return ['85%'];
    } else if (isOngoingMultipleSessions) {
      return [minSnapPoint - 10];
    }
    return [minSnapPoint, '100%'];
  }, [selectedOngoingSessionType, tabBarHeight, isOngoingMultipleSessions]);

  const isSheetExp = useMemo(
    () => !!selectedOngoingSessionType,
    [selectedOngoingSessionType]
  );

  const hasOngoingChargingSession = useMemo(
    () =>
      status === 'IN_PROGRESS' ||
      status === 'STOP_IN_QUEUE' ||
      status === 'START_IN_QUEUE',
    [status]
  );

  const hasOngoingSession = useMemo(
    () => !!ongoingParkingSession || hasOngoingChargingSession,
    [hasOngoingChargingSession, ongoingParkingSession]
  );

  const showOngoingParkingSession = useMemo(
    () => sessionToShow === 'PARKING',
    [sessionToShow]
  );

  const showOngoingChargingSession = useMemo(
    () => sessionToShow === 'CHARGING',
    [sessionToShow]
  );
  const prevOngoingParkingSheetExp = useRef(
    selectedOngoingSessionType === 'PARKING'
  );

  const updateLicensePlate = useCallback(() => {
    if (selectedOngoingSessionType === 'PARKING') {
      return ongoingParkingSession?.accessDevice?.value ?? '';
    }
    if (prevOngoingParkingSheetExp.current) {
      return undefined;
    }
    return parkingVehicleLicensePlate();
  }, [selectedOngoingSessionType, ongoingParkingSession]);
  // Synchronize the vehicle license plate with the parking sheet expansion state
  useEffect(() => {
    const licensePlate = updateLicensePlate();
    parkingVehicleLicensePlate(licensePlate);
    setParkingVehiclePlate(licensePlate);

    prevOngoingParkingSheetExp.current =
      selectedOngoingSessionType === 'PARKING';
  }, [
    selectedOngoingSessionType,
    ongoingParkingSession,
    setParkingVehiclePlate,
    updateLicensePlate,
  ]);

  useEffect(() => {
    // fetch right away & poll sessions every 15 secs on park screen focus
    // if it is web or have notification permision disabled
    const pollerCheck = async () => {
      const notificationPermission =
        await checkNotificationsPermissions('granted');
      if (
        isLoggedIn &&
        isFocused &&
        hasConnection &&
        (isWeb || !notificationPermission)
      ) {
        ongoingChargingSessionStartPolling(15000);
      } else {
        ongoingChargingSessionStopPolling();
      }
    };
    pollerCheck();
    return () => {
      ongoingParkingSessionStopPolling();
      ongoingChargingSessionStopPolling();
    };
  }, [
    hasConnection,
    isFocused,
    isLoggedIn,
    ongoingChargingSessionStartPolling,
    ongoingChargingSessionStopPolling,
  ]);

  if (ongoingParkingSessionError) {
    if (!ongoingParkingErrorShown) {
      setOngoingParkingErrorShown(true);
      showToast('error.showSessions', 'error');
    }
    ongoingParkingSessionStopPolling();
  }

  if (ongoingChargingSessionError) {
    if (!ongoingChargingErrorShown) {
      setOngoingChargingErrorShown(true);
      showToast('error.showCharging', 'error');
    }
    ongoingChargingSessionStopPolling();
  }

  useEffect(() => {
    if (!hasOngoingSession) {
      useParkingStore.getState().setSelectedOngoingSession(undefined);
    }
  }, [hasOngoingSession]);

  if (!showSheet || !hasOngoingSession) {
    return <></>;
  }

  let bottomInset = 0;
  if (isIos && isIosMini) {
    bottomInset = -6;
  } else if (isIos && isIosMax) {
    bottomInset = -22;
  } else if (isIos && !isIosMax && !isIosMini) {
    bottomInset = -11;
  } else if (isAndroid) {
    bottomInset = -10;
  }
  const handleSheetOpen = () => {
    if (showOngoingParkingSession) {
      useParkingStore.getState().setSelectedOngoingSession('PARKING');
    }
    if (showOngoingChargingSession) {
      useParkingStore.getState().setSelectedOngoingSession('CHARGING');
    }
  };

  const renderSessionContent = () => {
    if (isOngoingMultipleSessions) {
      return <OngoingMultipleSessionsHeader />;
    }

    if (showOngoingParkingSession) {
      return <OngoingParkingSheet />;
    }

    if (showOngoingChargingSession) {
      return <OngoingChargingSheet />;
    }

    return null;
  };

  return (
    <>
      <StatusBar barStyle={isSheetExp ? 'light-content' : 'default'} />
      <Sheet
        showBackDropIndex={1}
        snappingPoints={snappingPoints}
        bgColor={theme.colors.darkBg.grey}
        bottomInset={bottomInset}
        hideHandle
        onSheetClose={() => {
          useParkingStore.getState().setSelectedOngoingSession(undefined);
        }}
        onSheetOpen={handleSheetOpen}
        sheetMinHeight={25}
        sheetMaxHeight={isOngoingMultipleSessions && !isWeb ? 150 : undefined}
        borderRadius={theme.borderRadius.ongoingSheet}>
        {renderSessionContent()}
        <SheetState isMultiple={isOngoingMultipleSessions} />
      </Sheet>
    </>
  );
};

const SheetState = ({ isMultiple }: { isMultiple: boolean }) => {
  useOngoingSheetState(isMultiple);
  return <></>;
};

export default OngoingSessionsSheet;
